import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";

import { cn } from "../../utils/ui";

const avatarSizes = {
  xs: "size-6",
  sm: "size-8",
  md: "size-10",
  lg: "size-12",
  xl: "size-16",
  "2xl": "size-20",
} as const;

type AvatarSize = keyof typeof avatarSizes;

interface AvatarProps
  extends Omit<React.ComponentProps<typeof AvatarPrimitive.Root>, "asChild"> {
  /**
   * The image source for the avatar
   */
  src?: string;
  /**
   * The alt text for the avatar image
   */
  alt?: string;
  /**
   * Text or SVG to display as fallback when image fails to load
   */
  fallback?: React.ReactNode;
  /**
   * Size of the avatar
   * @default "sm"
   */
  size?: AvatarSize;
  /**
   * Delay in milliseconds before showing the fallback
   * @default 600
   */
  delayMs?: number;
  /**
   * Whether the image should be loaded or not
   * @default true
   */
  loadImage?: boolean;
  /**
   * Custom className for the root element
   */
  className?: string;
  /**
   * Custom className for the image element
   */
  imageClassName?: string;
  /**
   * Custom className for the fallback element
   */
  fallbackClassName?: string;
}

const Avatar = ({
  src,
  alt,
  fallback,
  size = "sm",
  delayMs = 600,
  loadImage = true,
  className,
  imageClassName,
  fallbackClassName,
  ...props
}: AvatarProps) => {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(
        "relative flex shrink-0 overflow-hidden rounded-full",
        avatarSizes[size],
        className
      )}
      {...props}
    >
      {src && loadImage && (
        <AvatarPrimitive.Image
          data-slot="avatar-image"
          src={src}
          alt={alt}
          className={cn(
            "aspect-square size-full border border-dark-50",
            imageClassName
          )}
        />
      )}
      <AvatarPrimitive.Fallback
        data-slot="avatar-fallback"
        delayMs={src ? delayMs : 0}
        className={cn(
          "bg-muted flex size-full items-center justify-center rounded-full border border-dark-50 font-montserrat",
          fallbackClassName
        )}
      >
        {fallback}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
};
interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Array of avatar elements to display
   */
  avatars: React.ReactNode[];
  /**
   * Maximum number of avatars to show before displaying a count
   * @default 3
   */
  maxVisible?: number;
  /**
   * Amount of overlap between avatars in pixels
   * @default -0.5rem
   */
  overlap?: string;
  /**
   * Size of the count indicator
   * @default "sm"
   */
  size?: AvatarSize;
  /**
   * Custom className for the count element
   */
  countClassName?: string;
  /**
   * Custom className for the root element
   */
  className?: string;
}

function AvatarGroup({
  avatars,
  maxVisible = 3,
  overlap = "-0.5rem",
  size = "sm",
  className,
  countClassName,
  ...props
}: AvatarGroupProps) {
  const visibleAvatars = avatars.slice(0, maxVisible);
  const remainingAvatars = Math.max(0, avatars.length - maxVisible);

  return (
    <div
      data-slot="avatar-group"
      className={cn("flex flex-row items-center", className)}
      {...props}
    >
      {visibleAvatars.map((avatar, index) => (
        <div
          key={index}
          className="relative"
          style={{
            marginLeft: index === 0 ? 0 : overlap,
            zIndex: visibleAvatars.length - index,
          }}
        >
          {avatar}
        </div>
      ))}
      {remainingAvatars > 0 && (
        <div
          className={cn(
            "text-white relative flex items-center justify-center rounded-full bg-primary-600 text-xs font-medium",
            avatarSizes[size],
            countClassName
          )}
          style={{ marginLeft: overlap, zIndex: 0 }}
        >
          +{remainingAvatars}
        </div>
      )}
    </div>
  );
}

export { Avatar, AvatarGroup };
